import type {
  MapLayerMouseEvent,
  ViewStateChangeEvent,
} from 'react-map-gl-v7/maplibre';
import type { SelectedTab } from './ProjectMapTabs';
import type { SingleTreeRegistration } from '@planet-sdk/common';
import type { ExtendedMapLibreMap, MapLibreRef } from '../../common/types/map';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import 'maplibre-gl/dist/maplibre-gl.css';
import Map, { NavigationControl } from 'react-map-gl-v7/maplibre';
import { useFetchLayers } from '../../../utils/mapsV2/useFetchLayers';
import MultipleProjectsView from './MultipleProjectsView';
import SingleProjectView from './SingleProjectView';
import {
  areMapCoordsEqual,
  calculateCentroid,
  centerMapOnCoordinates,
  getDeviceType,
  getFeaturesAtPoint,
  getInterventionInfo,
  getSitesGeoJson,
  getSiteIndex,
  getValidFeatures,
  INTERACTIVE_LAYERS,
  isPlantFeature,
} from '../../../utils/projectV2';
import MapControls from './MapControls';
import MapTabs from './ProjectMapTabs';
import MultiTreeInfo from '../ProjectDetails/components/MultiTreeInfo';
import SingleTreeInfo from '../ProjectDetails/components/SingleTreeInfo';
import styles from './ProjectsMap.module.scss';
import { useDebouncedEffect } from '../../../utils/useDebouncedEffect';
import { zoomOutMap } from '../../../utils/mapsV2/zoomToProjectSite';
import OtherInterventionInfo from '../ProjectDetails/components/OtherInterventionInfo';
import { PLANTATION_TYPES } from '../../../utils/constants/intervention';
import ExploreLayers from './ExploreLayers';
import WebGLGuard from '../../common/WebGLGuard';
import { clsx } from 'clsx';
import { useProjectMapStore } from '../../../stores/projectMapStore';
import { useQueryParamStore } from '../../../stores/queryParamStore';
import {
  useInterventionStore,
  useProjectStore,
  useSingleProjectStore,
  useViewStore,
} from '../../../stores';
import { useFilteredProjects } from '../../../hooks/useFilteredProjects';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/router';

const TimeTravel = dynamic(() => import('./TimeTravel'), {
  ssr: false,
  loading: () => <p>Loading comparison...</p>,
});

interface ProjectsMapProps {
  isMobile: boolean;
}

function ProjectsMap(props: ProjectsMapProps) {
  // Fetch layers data
  useFetchLayers();
  const { isMobile } = props;
  const locale = useLocale();
  const router = useRouter();
  const mapRef: MapLibreRef = useRef<ExtendedMapLibreMap | null>(null);
  // track last hovered intervention to avoid duplicate state updates
  const lastHoveredIdRef = useRef<string | null>(null);
  const { filteredProjects } = useFilteredProjects();
  // store: state
  const currentPage = useViewStore((state) => state.page);
  const isEmbedded = useQueryParamStore((state) => state.embed === 'true');
  const isQueryParamsLoaded = useQueryParamStore(
    (state) => state.isContextLoaded
  );
  const mapOptions = useProjectMapStore((state) => state.mapOptions);
  const timeTravelConfig = useProjectMapStore(
    (state) => state.timeTravelConfig
  );
  const viewState = useProjectMapStore((state) => state.viewState);
  const mapState = useProjectMapStore((state) => state.mapState);
  const projects = useProjectStore((state) => state.projects);
  const singleProject = useSingleProjectStore((state) => state.singleProject);
  const selectedSampleIntervention = useInterventionStore(
    (state) => state.selectedSampleIntervention
  );
  const selectedIntervention = useInterventionStore(
    (state) => state.selectedIntervention
  );
  const interventions = useInterventionStore((state) => state.interventions);
  // store: action
  const initializeMapStyle = useProjectMapStore(
    (state) => state.initializeMapStyle
  );
  const setTimeTravelConfig = useProjectMapStore(
    (state) => state.setTimeTravelConfig
  );
  const handleViewStateChange = useProjectMapStore(
    (state) => state.handleViewStateChange
  );
  const setMapState = useProjectMapStore((state) => state.setMapState);
  const setSelectedSampleIntervention = useInterventionStore(
    (state) => state.setSelectedSampleIntervention
  );
  const selectSiteAndSyncUrl = useSingleProjectStore(
    (state) => state.selectSiteAndSyncUrl
  );
  const setHoveredIntervention = useInterventionStore(
    (state) => state.setHoveredIntervention
  );
  const selectInterventionSyncUrl = useInterventionStore(
    (state) => state.selectInterventionSyncUrl
  );

  const [selectedTab, setSelectedTab] = useState<SelectedTab | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [wasTimeTravelMounted, setWasTimeTravelMounted] = useState(false);

  const sitesGeoJson = useMemo(
    () => getSitesGeoJson(singleProject?.sites ?? []),
    [singleProject?.sites]
  );
  const isExploreMode = useMemo(() => {
    const enabledLayers = Object.entries(mapOptions).filter(
      ([key, value]) => key !== 'projects' && value === true
    );
    return enabledLayers.length > 0;
  }, [mapOptions]);

  useEffect(() => {
    initializeMapStyle();
  }, [initializeMapStyle]);

  useEffect(() => {
    if (!mapLoaded) return;
    // Ensure the map resizes properly once it's fully loaded.
    mapRef.current?.resize();
  }, [mapLoaded]);

  useEffect(() => {
    if (currentPage === 'project-details') {
      setSelectedTab('field');
    } else {
      setTimeTravelConfig(null);
      setSelectedTab(null);
      setWasTimeTravelMounted(false);
    }
  }, [currentPage]);

  useEffect(() => {
    if (selectedTab === 'timeTravel') {
      setWasTimeTravelMounted(true);
    }
  }, [selectedTab]);

  useEffect(() => {
    // Update mapState when embed status changes, but only after query params are loaded
    if (isQueryParamsLoaded) {
      setMapState({ scrollZoom: !isEmbedded });
    }
  }, [isQueryParamsLoaded, isEmbedded, setMapState]);

  useDebouncedEffect(
    () => {
      const map = mapRef.current;
      const shouldCenterMap =
        filteredProjects.length > 0 &&
        (filteredProjects.length < 30 ||
          filteredProjects.length === projects?.length) &&
        map !== null &&
        currentPage === 'project-list';

      if (!shouldCenterMap) return;
      const validFeatures = getValidFeatures(filteredProjects);
      if (validFeatures.length === 0) return;

      const centroid = calculateCentroid(validFeatures);
      if (!centroid.geometry) return;

      const currentCenter = map.getCenter();
      if (areMapCoordsEqual(currentCenter, centroid.geometry.coordinates))
        return;

      centerMapOnCoordinates(mapRef, centroid.geometry.coordinates);
    },
    250,
    [filteredProjects]
  );
  const shouldShowSingleProjectsView =
    singleProject !== null && currentPage === 'project-details' && mapLoaded;
  const shouldShowMultipleProjectsView =
    currentPage === 'project-list' &&
    Boolean(mapOptions.projects) &&
    projects &&
    projects.length > 0 &&
    !shouldShowSingleProjectsView &&
    mapLoaded;
  const shouldShowMultiTreeInfo =
    isMobile &&
    selectedSampleIntervention === null &&
    selectedIntervention?.type === 'multi-tree-registration';
  const shouldShowSingleTreeInfo =
    isMobile &&
    (selectedSampleIntervention !== null ||
      selectedIntervention?.type === 'single-tree-registration');
  const shouldShowNavigationControls = !(
    shouldShowMultiTreeInfo || shouldShowSingleTreeInfo
  );
  const isTimeTravelEnabled =
    shouldShowSingleProjectsView &&
    timeTravelConfig !== null &&
    timeTravelConfig.sources !== null &&
    timeTravelConfig.projectId === singleProject?.id &&
    !isMobile;
  const shouldShowTimeTravel =
    isTimeTravelEnabled &&
    (selectedTab === 'timeTravel' || wasTimeTravelMounted);
  const shouldShowMapTabs = selectedTab !== null;
  const shouldShowExploreLayers =
    currentPage === 'project-list' && isExploreMode;

  const mobileOS = useMemo(() => getDeviceType(), [isMobile]);

  useEffect(() => {
    if (currentPage === 'project-details' || !mapLoaded) return;

    if (mapRef.current) {
      const map = mapRef.current.getMap
        ? mapRef.current.getMap()
        : mapRef.current;

      try {
        zoomOutMap(map, () => {
          handleViewStateChange({
            ...map.getCenter(),
            zoom: map.getZoom(),
          });
        });
      } catch (err) {
        console.error('Failed to zoom out map:', err);
      }
    }
  }, [currentPage, mapLoaded]);

  const onMove = useCallback(
    (evt: ViewStateChangeEvent) => {
      handleViewStateChange(evt.viewState);
    },
    [handleViewStateChange]
  );

  const onMouseMove = useCallback(
    (e: MapLayerMouseEvent) => {
      if (currentPage !== 'project-details') return;

      // Clear any existing hover highlight (no-op when nothing is hovered).
      const clearHover = () => {
        if (lastHoveredIdRef.current !== null) {
          lastHoveredIdRef.current = null;
          setHoveredIntervention(null);
        }
      };

      const features = getFeaturesAtPoint(mapRef, e.point);
      if (!features?.length) {
        clearHover();
        return;
      }

      // Map libraries typically return features ordered by render stack,
      // where the first item represents the topmost visible layer at the point.
      // Moving onto a non-plant feature (e.g. a site polygon) must also clear
      // any existing hover, otherwise the previous highlight stays stuck.
      if (!isPlantFeature(features[0])) {
        clearHover();
        return;
      }

      const hoveredId = features[0].properties?.id ?? null;

      // Don't layer a hover style over the already-selected intervention.
      if (hoveredId !== null && hoveredId === selectedIntervention?.id) {
        clearHover();
        return;
      }

      if (hoveredId === lastHoveredIdRef.current) return;

      const newIntervention = getInterventionInfo(interventions, features[0]);
      const newId = newIntervention?.id ?? null;

      lastHoveredIdRef.current = newId;
      setHoveredIntervention(newIntervention ?? null);
    },
    [interventions, currentPage, selectedIntervention]
  );
  /**
   * Map click handler invoked when the user clicks on the map in
   * 'project-details' ('project-list' results in an early return).
   * Not invoked while clicking SampleTreeMarkers, as propagation is stopped there.
   *
   * Behavior:
   * - Clicking a new intervention (point or polygon) selects it.
   * - Clicking the already-selected point intervention again deselects it and
   *   selects the site underneath again (or none when not over a site).
   * - Clicking a site polygon selects that site.
   * - Any click clears a selected sample intervention first.
   */
  const onClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (currentPage !== 'project-details') return;

      const features = getFeaturesAtPoint(mapRef, e.point);
      if (!features || features.length === 0) return;

      const sites = singleProject?.sites || [];
      const projectSlug = singleProject?.slug ?? '';
      const hasSites = sites.length > 0;
      const siteIndex = hasSites ? getSiteIndex(sites, features) : null;

      // Deselect sample intervention when clicking the parent intervention (polygon)
      if (selectedSampleIntervention) setSelectedSampleIntervention(null);

      if (isPlantFeature(features[0])) {
        const isSameIntervention =
          selectedIntervention !== null &&
          features[0].properties?.id === selectedIntervention.id;

        if (isSameIntervention) {
          // Clicking a selected point intervention again deselects it and reselects
          // the underlying site (if any). Polygon interventions remain selected.
          if (selectedIntervention.geometry.type === 'Point') {
            const underlyingSiteIndex =
              siteIndex !== null && siteIndex >= 0 ? siteIndex : null;
            selectSiteAndSyncUrl(underlyingSiteIndex, locale, router);
          }
          return;
        }

        const newIntervention = getInterventionInfo(interventions, features[0]);

        // Clicking a different intervention → select it
        if (newIntervention) {
          selectInterventionSyncUrl(
            newIntervention,
            locale,
            projectSlug,
            router
          );
          return;
        }
      }

      // Otherwise, handle site selection
      if (siteIndex !== null && siteIndex >= 0) {
        selectSiteAndSyncUrl(siteIndex, locale, router);
      }
    },
    [
      interventions,
      currentPage,
      selectedIntervention,
      singleProject,
      selectedSampleIntervention,
    ]
  );

  const mapControlProps = {
    selectedTab,
    isMobile,
    currentPage,
    mobileOS,
  };

  const singleProjectViewProps = {
    mapRef,
    selectedTab,
    sitesGeoJson,
  };

  const shouldShowOtherIntervention =
    isMobile &&
    selectedIntervention !== null &&
    !PLANTATION_TYPES.includes(selectedIntervention.type);

  return (
    <>
      <MapControls {...mapControlProps} />

      <WebGLGuard>
        <div
          className={clsx(styles.mapContainer, mobileOS && styles[mobileOS])}
        >
          {shouldShowMapTabs && (
            <MapTabs
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              isTimeTravelEnabled={isTimeTravelEnabled}
            />
          )}
          {shouldShowTimeTravel && (
            <div>
              <TimeTravel
                sitesGeoJson={sitesGeoJson}
                isVisible={selectedTab === 'timeTravel'}
              />
            </div>
          )}
          <Map
            {...viewState}
            {...mapState}
            onMove={onMove}
            onLoad={() => setMapLoaded(true)}
            onMouseMove={onMouseMove}
            onMouseOut={() => setHoveredIntervention(null)}
            onClick={onClick}
            attributionControl={false}
            ref={mapRef}
            interactiveLayerIds={
              singleProject !== null ? INTERACTIVE_LAYERS : undefined
            }
            style={{ width: '100%', height: '100%' }}
          >
            {shouldShowExploreLayers && <ExploreLayers />}
            {shouldShowSingleProjectsView && (
              <SingleProjectView {...singleProjectViewProps} />
            )}
            {shouldShowMultipleProjectsView && <MultipleProjectsView />}
            {shouldShowNavigationControls && (
              <NavigationControl position="bottom-right" showCompass={false} />
            )}
          </Map>
        </div>
      </WebGLGuard>
      {shouldShowMultiTreeInfo && (
        <MultiTreeInfo
          activeMultiTree={selectedIntervention}
          isMobile={isMobile}
        />
      )}
      {shouldShowSingleTreeInfo && (
        <SingleTreeInfo
          activeSingleTree={
            selectedSampleIntervention ||
            (selectedIntervention as SingleTreeRegistration)
          }
          isMobile={isMobile}
        />
      )}
      {shouldShowOtherIntervention && (
        <OtherInterventionInfo isMobile={isMobile} />
      )}
    </>
  );
}

export default ProjectsMap;
